import { convertDateToTimeZone } from 'infrastructure/functions';
import StatusCell from 'components/molecules/rpm-overview-table/components/reading-table/status-cell';
import ReadingCell from 'components/molecules/rpm-overview-table/components/reading-table/reading-cell';
import ThresholdCell from 'components/molecules/rpm-overview-table/components/reading-table/threshold-cell';
import DateCell from 'components/molecules/rpm-overview-table/components/reading-table/date-cell';
import { useTranslation } from 'react-i18next';
import BaseCheckbox from 'components/atoms/base-checkbox';

import s from './styles.module.scss';

import type { Dispatch, SetStateAction, FC } from 'react';
import type { IActivityData } from 'infrastructure/interfaces/i-rpm-patient-activity';

export const getReadingId = (r: IActivityData): `${string}-${string}` =>
  `${r.deviceId}-${r.id}`;

interface ReadingsListProps {
  readings?: IActivityData[];
  selectedReadings: IActivityData[];
  setSelectedReadings: Dispatch<SetStateAction<IActivityData[]>>;
}

const ReadingsList: FC<ReadingsListProps> = ({
  readings,
  selectedReadings,
  setSelectedReadings,
}) => {
  const { t } = useTranslation();

  return readings?.map((reading, index) => {
    const readingDate = new Date(parseInt(reading.createdAt));
    const patientTimeDate = convertDateToTimeZone(
      new Date(parseInt(reading.createdAt)),
      reading.patient.timezone,
    );
    const ordinalNumber = index + 1;
    const readingId = getReadingId(reading);
    const isChecked = selectedReadings.some(
      (i) => getReadingId(i) === readingId,
    );

    const onChange = (r: IActivityData) => {
      const existReading = selectedReadings.some(
        (i) => getReadingId(i) === getReadingId(r),
      );
      if (existReading) {
        setSelectedReadings((prev) =>
          prev.filter((i) => getReadingId(i) !== getReadingId(r)),
        );
      } else {
        setSelectedReadings((prev) => [...prev, reading]);
      }
    };

    return (
      <div className={s['reading-list-item-wrapper']} key={readingId}>
        <div className={s['reading-list-header']}>
          <div className={s['reading-list-header-left']}>
            <div className={s['reading-list-date']}>
              <b>#{ordinalNumber} </b>
              <DateCell date={readingDate} />
            </div>
            <div className={s['reading-list-date']}>
              <span>{t('labels.patientTime')}: </span>
              <DateCell date={patientTimeDate} />
            </div>
          </div>
          <div className={s['reading-list-header-right']}>
            <BaseCheckbox
              value={isChecked}
              onChange={() => onChange(reading)}
            />
          </div>
        </div>
        <div className={s['reading-list-body']}>
          <div className={s['reading-list-reading']}>
            <StatusCell
              globalStatus={reading.globalStatus}
              thresholds={reading.patient.threshold}
              thresholdName={reading.thresholdName}
              mightBeErroneousReason={reading.mightBeErroneousReason}
              isRecovered={reading.isRecovered}
              readingType={reading.readingType}
            />
            <ReadingCell
              values={reading.value}
              statuses={reading.statuses}
              readingType={reading.readingType}
            />
          </div>
          <div className={s['reading-list-threshold']}>
            <ThresholdCell
              values={reading.value}
              readingType={reading.readingType}
              thresholds={reading.patient.threshold}
              statuses={reading.statuses}
              globalStatus={reading.globalStatus}
              thresholdName={reading.thresholdName}
            />
          </div>
        </div>
      </div>
    );
  });
};

export default ReadingsList;
